import { useEffect, useMemo, useState } from 'react'
import EstoqueHttp from '../../../../services/api/https/estoque/Estoque.http'
import { errorHandler } from '../../../../services/util/error.service'
import { gerarArquivoExcel } from '../../../../services/util/excel.service'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  LOADING: 'Carregando relatório',
}

function getUniqueOptions(rows, field) {
  return Array.from(new Set(rows.map(r => r[field]).filter(Boolean)))
    .sort()
    .map(v => ({ label: v, value: v }))
}

function getUniqueProdutoOptions(rows) {
  return Array.from(
    new Set(rows.map(r => `${r.product_code} - ${r.product_name}`).filter(Boolean)),
  )
    .sort()
    .map(v => ({ label: v, value: v }))
}

const INITIAL_STATE = {
  allRows: [],
  filteredRows: [],
  globalFilter: '',
  selectedEstoques: [],
  selectedPosicoes: [],
  selectedEmpresas: [],
  selectedProdutos: [],
  estoqueOptions: [],
  posicaoOptions: [],
  empresaOptions: [],
  produtoOptions: [],
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
}

export default function useRelatorioEstoquePorProduto() {
  const [state, setState] = useState({ ...INITIAL_STATE })

  // Recalcula linhas filtradas e opções de cascata sempre que allRows ou filtros mudarem
  useEffect(() => {
    let filtered = state.allRows

    if (state.selectedEstoques.length > 0) {
      filtered = filtered.filter(r => state.selectedEstoques.includes(r.estoque_name))
    }
    const posicaoOptions = getUniqueOptions(filtered, 'endereco')

    if (state.selectedPosicoes.length > 0) {
      filtered = filtered.filter(r => state.selectedPosicoes.includes(r.endereco))
    }
    const empresaOptions = getUniqueOptions(filtered, 'company_name')

    if (state.selectedEmpresas.length > 0) {
      filtered = filtered.filter(r => state.selectedEmpresas.includes(r.company_name))
    }
    const produtoOptions = getUniqueProdutoOptions(filtered)

    if (state.selectedProdutos.length > 0) {
      filtered = filtered.filter(r =>
        state.selectedProdutos.includes(`${r.product_code} - ${r.product_name}`),
      )
    }

    setState(prev => ({
      ...prev,
      filteredRows: filtered,
      posicaoOptions,
      empresaOptions,
      produtoOptions,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.allRows,
    state.selectedEstoques,
    state.selectedPosicoes,
    state.selectedEmpresas,
    state.selectedProdutos,
  ])

  const localDispatch = useMemo(
    () => ({
      async onInit(selectedTp) {
        if (!selectedTp) return
        setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.LOADING }))
        try {
          const rows = await EstoqueHttp.getRelatorioEstoquePorProduto(selectedTp)
          setState(prev => ({
            ...prev,
            allRows: rows,
            filteredRows: rows,
            estoqueOptions: getUniqueOptions(rows, 'estoque_name'),
            posicaoOptions: getUniqueOptions(rows, 'endereco'),
            empresaOptions: getUniqueOptions(rows, 'company_name'),
            produtoOptions: getUniqueProdutoOptions(rows),
            selectedEstoques: [],
            selectedPosicoes: [],
            selectedEmpresas: [],
            selectedProdutos: [],
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        } catch (err) {
          setState(prev => ({
            ...prev,
            errorMessage: errorHandler(err),
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        }
      },

      onGlobalFilterChange(value) {
        setState(prev => ({ ...prev, globalFilter: value }))
      },

      exportExcel(filteredRows) {
        if (!filteredRows || filteredRows.length === 0) return

        const colunas = [
          [
            'Estoque',
            'Posição',
            'Empresa',
            'Código',
            'Produto',
            'QTD Atual',
            'Estoque Mínimo',
            'Validade',
            'Lote',
          ],
        ]

        filteredRows.forEach(r => {
          colunas.push([
            r.estoque_name || '',
            r.endereco || '',
            r.company_name || '',
            r.product_code || '',
            r.product_name || '',
            r.quantidade != null ? Number(r.quantidade) : '',
            r.estoque_minimo != null ? Number(r.estoque_minimo) : '',
            r.validade || '',
            r.lote || '',
          ])
        })

        gerarArquivoExcel('Relatório', colunas, 'Relatorio_Estoque_Produto.xlsx')
      },
    }),
    [],
  )

  return { state, setState, localDispatch }
}
